import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { get, orderBy, template } from 'lodash';
import { Grid, Row } from 'react-bootstrap';

import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchChannel, fetchChannelCallouts, requestChannelUpsell, showedChannelUpsell } from 'services/channel/duck';
import { fetchSemCampaignSummary, fetchSemConversionSummary, fetchSemKeywordSummary, fetchSemAdgroupSummary } from 'services/sem/duck';
import { formatPercentage } from 'helpers/formatNumbers';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import { validateStatisticsData } from 'helpers/validate';
import { generateCalloutComponents } from 'helpers/componentGenerators';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { prefixKeywordsWithSpace } from 'helpers/strings';

// Components and Containers
import LayerThreeDashboardComponent from 'containers/dashboards/DashboardBaseComponent';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import TileWithValidation from 'components/molecules/TileWithValidation';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import ExportPDF from 'containers/buttons/ExportPDF';
import ChannelUpsell from 'components/molecules/ChannelUpsell';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';
import DividerLine from 'components/atoms/DividerLine';
import SubModule from 'components/organisms/SubModule';

// Charts
import StackedAreaChart from 'components/molecules/charts/StackedAreaChart';
import BarChart from 'components/molecules/charts/BarChart';
import LineChart from 'components/molecules/charts/LineChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';

// Tables
import CommonSummaryTable from 'components/molecules/tables/CommonSummaryTable';

const DividerLineStyled = styled(DividerLine)`
    margin-bottom: 32px;
`;

function percentageToolTipFormatter(format) {
    return template(format, {
        interpolate: /{([\s\S]+?)}/g
    })({
        series: this.series,
        point: {
            ...this,
            y: formatPercentage(this.y)
        }
    });
}

function percentageFormatter() {
    return formatPercentage(this.value);
}

class LayerThreeSEMContainer extends LayerThreeDashboardComponent {
    constructor(props) {
        super(props, 'sem');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();

        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entities.meta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchSemKeywordSummary(payload);
        this.props.fetchSemAdgroupSummary(payload);
        this.props.fetchSemCampaignSummary(payload);
        this.props.fetchSemConversionSummary(payload);
    }

    render() {
        const stub = `l3-${this.getCurrentChannel()}`;
        const {
            backfillBuckets,
            entityObjective,
            entityObjectiveLabelCode,
            xAxisLabel,
            selectedTimeRange,
            hideBudgetTimeCharts,
            showSemCpconv,
            showConversionRevenue
        } = this.props;

        const {
            conversionTermSingular,
            conversionTermPlural,
            isLabelCoded
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const hasChannelData = get(this.props, 'channel.hasData', false);
        const hasCalloutData = get(this.props, 'channel.callouts.params.hasData', false);
        const hasCampaignSummaryData = get(this.props, 'campaignSummary.hasData', false);
        const hasAdgroupSummaryData = get(this.props, 'adgroupSummary.hasData', false);
        const hasKeywordSummaryData = get(this.props, 'keywordSummary.hasData', false);
        const hasConversionSummaryData = get(this.props, 'conversionSummary.hasData', false);

        const callouts = get(this.props, 'channel.callouts.data', {});
        const data = get(this.props, 'channel.data', {});
        const calloutWindow = get(this.props, 'channel.callouts.params.timeRanges.previous.windowSize', null);
        const keywordSummaryData = prefixKeywordsWithSpace(get(this.props, 'keywordSummary.data', []));

        const sortedKeywords =
            orderBy(keywordSummaryData, ['clicks'], ['desc']);

        const rawCampaignSummaryData = get(this.props, 'campaignSummary.data', []);
        const campaignSummaryData = rawCampaignSummaryData.filter(val => val.advertising_channel_type !== 'Display');
        const sortedCampaignSummaryData =
            hasCampaignSummaryData ? orderBy(campaignSummaryData, ['clicks'], ['desc']) : [];

        const rawAdgroupSummaryData = get(this.props, 'adgroupSummary.data', []);
        const adgroupSummaryData = rawAdgroupSummaryData.filter(val => val.advertising_channel_sub_type !== 'Gmail Ad campaign' && val.advertising_channel_type !== 'Display');
        const sortedAdgroupSummaryData =
            hasAdgroupSummaryData ? orderBy(adgroupSummaryData, ['clicks'], ['desc']) : [];

        const gmailSummaryData = rawAdgroupSummaryData.filter(val => val.advertising_channel_sub_type === 'Gmail Ad campaign');
        const hasGmailSummaryData = Boolean(gmailSummaryData.length);
        const sortedGmailSummaryData =
            hasGmailSummaryData ? orderBy(gmailSummaryData, ['gmail_secondary_clicks'], ['desc']) : [];

        const gdnAdgroupSummaryData = orderBy(
            rawAdgroupSummaryData.filter(val => val.advertising_channel_type === 'Display' && val.advertising_channel_sub_type !== 'Gmail Ad campaign'),
            ['clicks'], ['desc']
        );
        const hasGdnAdgroupSummaryData = Boolean(gdnAdgroupSummaryData.length);

        const conversionSummaryData = get(this.props, 'conversionSummary.data', []);
        const sortedConversionSummaryData =
            hasConversionSummaryData ? orderBy(conversionSummaryData, ['conversions'], ['desc']) : [];
        const showConversionBreakdown = conversionSummaryData.length > 1;

        const calloutSettings = [
            {
                key: 'clicks',
                title: 'Clicks',
                format: '0,0'
            },
            {
                key: 'ctr',
                title: 'Click Through Rate',
                format: '0.00%'
            },
            {
                key: 'cpc',
                title: 'Cost per Click',
                format: '$0.00'
            },
            {
                key: 'conversion_rate',
                title: `${conversionTermSingular} Rate`,
                format: '0.00%'
            },
            showSemCpconv ? {
                key: 'conversions',
                title: conversionTermPlural,
                format: '0,0'
            } : null,
            showSemCpconv ? {
                key: 'cpconv',
                title: `Cost per ${conversionTermSingular}`,
                format: '$0.00'
            } : null
        ].filter(e => e);

        const mdSizeOverride = calloutSettings.length > 4 ? 4 : undefined;
        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, mdSizeOverride, isLabelCoded
        });

        const isChannelAvailable = get(this.props, 'entities.meta.channelAvailability.sem', false);

        const upsellSettings = this.props.entityMeta.upsellSettings;
        const upsellRequested = get(this.props, 'entityMeta.upsellRequested', {});

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    SEM
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs     */}
                <Row>
                    {calloutComponents}
                </Row>

                {/* Charts */}

                {hasChannelData ? (
                    <TwoAxisBarWithLineChart
                        title={`CLICKS & ${conversionTermPlural.toUpperCase()}`}
                        exportName={`${stub}-clicks-&-conversions`}
                        data={data}
                        yBar="clicks"
                        yLine="conversions"
                        yLineTitle={conversionTermPlural}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasChannelData ? (
                    <LineChart
                        title={`${conversionTermSingular.toUpperCase()} RATE TREND`}
                        exportName={`${stub}-conversion-rate-trend`}
                        data={{ [this.state.channel]: data }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yTitle={`${conversionTermSingular} Rate`}
                        yOptions={{
                            tooltip: {
                                pointFormatter: percentageToolTipFormatter
                            }
                        }}
                        yAxisOptions={{
                            labels: {
                                formatter: percentageFormatter
                            }
                        }}
                        y="conversion_rate"
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasChannelData} />
                )}

                {hasConversionSummaryData && showConversionBreakdown ? (
                    <BarChart
                        title={`TOP SEARCH ${conversionTermPlural.toUpperCase()}`}
                        exportName={`${stub}-conversion-breakdown`}
                        data={sortedConversionSummaryData}
                        showTopOnly
                        y="conversions"
                        yAxisLabel={conversionTermPlural}
                        color={this.props.theme.orderedColors[0]}
                        animateDuration={2000}
                        truncateValues={true}
                        categories={sortedConversionSummaryData.map(c => c.conversion_name)} />
                ) : (
                    <RenderingTile height='400px' show={hasConversionSummaryData && showConversionBreakdown} />
                )}

                <CommonSummaryTable
                    title={`${conversionTermSingular.toUpperCase()} SUMMARY`}
                    exportName={`${stub}-conversion-summary`}
                    keyField="conversionName"
                    defaultSorted={[{
                        id: 'conversions',
                        desc: true
                    }]}
                    conversionTermSingular={conversionTermSingular}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    hasData={hasConversionSummaryData}
                    accessors={['conversionName', 'conversions']}
                    data={sortedConversionSummaryData}
                />
                {!!campaignSummaryData.length &&
                    <SubModule title='By Search Campaign' hasData={hasCampaignSummaryData}>
                        {hasCampaignSummaryData ? (
                            <BarChart
                                title="TOP CLICKS BY SEARCH CAMPAIGN"
                                exportName={`${stub}-clicks-by-search-campaign`}
                                data={sortedCampaignSummaryData}
                                showTopOnly
                                y="clicks"
                                yAxisLabel="Clicks"
                                color={this.props.theme.orderedColors[0]}
                                animateDuration={2000}
                                categories={sortedCampaignSummaryData.map(c => c.campaign_name)} />
                        ) : (
                            <RenderingTile height='400px' show={hasCampaignSummaryData} />
                        )}

                        <CommonSummaryTable
                            title="SEARCH CAMPAIGN SUMMARY"
                            exportName={`${stub}-search-campaign-summary`}
                            keyField="campaignName"
                            tableType="sem"
                            entityObjectiveLabelCode={entityObjectiveLabelCode}
                            showSemCpconv={showSemCpconv}
                            showConversionRevenue={showConversionRevenue}
                            hasData={hasCampaignSummaryData}
                            data={campaignSummaryData}
                        />
                    </SubModule>
                }

                {!!adgroupSummaryData.length &&
                    <SubModule title='By Search Ad Group' hasData={hasAdgroupSummaryData}>
                        {hasAdgroupSummaryData ? (
                            <BarChart
                                title="TOP CLICKS BY SEARCH AD GROUP"
                                exportName={`${stub}-clicks-by-search-adgroup`}
                                data={sortedAdgroupSummaryData}
                                showTopOnly
                                y="clicks"
                                yAxisLabel="Clicks"
                                color={this.props.theme.orderedColors[0]}
                                animateDuration={2000}
                                categories={sortedAdgroupSummaryData.map(c => c.adgroup_name)} />
                        ) : (
                            <RenderingTile height='400px' show={hasAdgroupSummaryData} />
                        )}

                        <CommonSummaryTable
                            title="SEARCH AD GROUP SUMMARY"
                            exportName={`${stub}-search-adgroup-summary`}
                            keyField="adgroupName"
                            tableType="sem"
                            entityObjectiveLabelCode={entityObjectiveLabelCode}
                            showSemCpconv={showSemCpconv}
                            showConversionRevenue={showConversionRevenue}
                            hasData={hasAdgroupSummaryData}
                            data={sortedAdgroupSummaryData}
                        />
                    </SubModule>
                }

                <SubModule title='By Google Display Network (GDN) Ad Group' hasData={!hasGdnAdgroupSummaryData ? 'HIDE_SECTION' : hasGdnAdgroupSummaryData}>
                    {hasGdnAdgroupSummaryData ? (
                        <BarChart
                            title="TOP CLICKS BY GDN AD GROUP"
                            exportName={`${stub}-clicks-by-gdn-adgroup`}
                            data={gdnAdgroupSummaryData}
                            showTopOnly
                            y="clicks"
                            yAxisLabel="Clicks"
                            color={this.props.theme.orderedColors[0]}
                            animateDuration={2000}
                            categories={gdnAdgroupSummaryData.map(c => c.adgroup_name)} />
                    ) : (
                        <RenderingTile height='400px' show={hasGdnAdgroupSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="GDN AD GROUP SUMMARY"
                        exportName={`${stub}-gdn-adgroup-summary`}
                        keyField="adgroupName"
                        tableType="sem"
                        showSemCpconv={showSemCpconv}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        hasData={hasGdnAdgroupSummaryData}
                        data={gdnAdgroupSummaryData}
                    />
                </SubModule>

                <SubModule title='By Keyword' hasData={hasKeywordSummaryData}>
                    {hasKeywordSummaryData ? (
                        <TwoAxisBarWithScatterChart
                            title="TOP KEYWORDS"
                            exportName={`${stub}-top-keywords`}
                            data={sortedKeywords}
                            showTopOnly
                            yBar="clicks"
                            scatter="cpc"
                            scatterTitle="CPC"
                            toolTipOptions={{
                                pointFormatter: toolTipPointFormatterGenerator
                            }}
                            yScatterOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('priceWithoutSpace')
                                }
                            }}
                            yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                            animateDuration={2000}
                            tactic={this.state.channel}
                            categories={sortedKeywords.map(k => k.keyword)} />
                    ) : (
                        <RenderingTile height='400px' show={hasKeywordSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="KEYWORD SUMMARY"
                        exportName={`${stub}-keyword-summary`}
                        keyField="keyword"
                        tableType="sem"
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        showSemCpconv={showSemCpconv}
                        showConversionRevenue={showConversionRevenue}
                        hasData={hasKeywordSummaryData}
                        data={keywordSummaryData}
                    />
                </SubModule>

                <SubModule
                    title='Gmail'
                    hasData={hasGmailSummaryData === false ? 'HIDE_SECTION' : hasGmailSummaryData}
                >
                    {hasGmailSummaryData ? (
                        <TwoAxisBarWithScatterChart
                            title="TOP CLICKS TO SITE BY AD GROUP"
                            exportName={`${stub}-clicks-to-site-by-adgroup`}
                            data={sortedGmailSummaryData}
                            showTopOnly
                            yBar="gmail_secondary_clicks"
                            yBarTitle="Clicks to Site"
                            scatter="gmail_secondary_clicks_rate"
                            scatterTitle="Clicks to Site Rate"
                            toolTipOptions={{
                                pointFormatter: toolTipPointFormatterGenerator
                            }}
                            yScatterOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('percentage')
                                }
                            }}
                            yScatterUserOptions={{ formatByType: 'percentage' }}
                            animateDuration={2000}
                            categories={sortedGmailSummaryData.map(c => c.adgroup_name)} />
                    ) : (
                        <RenderingTile height='400px' show={hasGmailSummaryData} />
                    )}

                    <CommonSummaryTable
                        title="GMAIL SUMMARY"
                        exportName={`${stub}-gmail-summary`}
                        keyField="adgroupName"
                        tableType="gmail"
                        hasData={hasGmailSummaryData}
                        data={gmailSummaryData}
                    />
                </SubModule>

                <DividerLineStyled />

                <TileWithValidation
                    hasData={hasChannelData && !hideBudgetTimeCharts}
                    innerComponent={StackedAreaChart}
                    innerProps={{
                        title: 'BUDGET REPORT',
                        exportName: `${stub}-budget-report`,
                        data: { [this.state.channel]: data },
                        y: 'spend',
                        xAxisLabel,
                        backfillBuckets,
                        yAxisLabel: 'Budget',
                        yOptions: { formatByType: 'priceWithoutSpace' },
                        toolTipOptions: { pointFormatter: toolTipPointFormatterGenerator },
                        yAxisOptions: {
                            labels: {
                                formatter: labelFormatterGenerator('priceWithoutSpace')
                            }
                        },
                        animateDuration: 2000,
                        xAxisType: 'datetime',
                        validation: {
                            key: 'spend',
                            data: this.props.channel.statistics,
                            validator: validateStatisticsData
                        }
                    }}
                />
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    SEM
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {upsellSettings.enabled && upsellSettings.sem ? (
                    <ChannelUpsell
                        channel='sem'
                        channelDisplay='SEM'
                        icon='icon-sem-fill'
                        successfulRequest={upsellRequested.sem}
                        buttonClick={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.requestChannelUpsell({ entityId, channel });
                        }}
                        onMount={() => {
                            const entityId = this.getCurrentEntityId();
                            const channel = this.getCurrentChannel();
                            return this.props.showedChannelUpsell({ entityId, channel });
                        }}
                    />
                ) : (
                    <NoChannelDataTile channel='SEM' />
                )}
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entities: state.entities,
    entityMeta: state.entities.meta,
    channel: state.channel.sem,
    keywordSummary: state.sem.keyword.summary,
    adgroupSummary: state.sem.adgroup.summary,
    campaignSummary: state.sem.campaign.summary,
    conversionSummary: state.sem.conversion.summary,
    selectedTimeRange: state.config.selectedTimeRange,
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showSemCpconv: get(state, 'entities.meta.meta.showSemCpconv', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),
    hideBudgetTimeCharts: get(state, 'entities.meta.meta.hideBudgetTimeCharts', false),
});

export default connect(mapStateToProps, {
    fetchEntityMeta,
    fetchChannel,
    fetchChannelCallouts,
    fetchSemKeywordSummary,
    fetchSemAdgroupSummary,
    fetchSemCampaignSummary,
    fetchSemConversionSummary,
    requestChannelUpsell,
    showedChannelUpsell,
})(LayerThreeSEMContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerThreeSEMContainer.js